import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import type { AxiosError, AxiosResponse } from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import Cookies from 'js-cookie'
import Pusher from 'pusher-js'
import type { ReactNode, ReactPortal } from 'react'
import React, { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import type { ToastContentProps } from 'react-toastify'
import { toast } from 'react-toastify'

import LoadingSpinner from './components/LoadingSpinner'
import { persistor } from './store'
import { apiService } from './store/apiService'
import {
  removeFailedImage,
  setImageUploadFailed,
  setIsMultiSession,
} from './store/appSlice'
import { setGalleryImageLodaingMap } from './store/sessionSlice'
import { clearSession } from './utils/appApi'
import { ErrorCodes } from './utils/constants'
import httpService, { resetHeaders } from './utils/httpService'

const AxiosInterceptor = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth0()
  const { loginWithRedirect, isAuthenticated, isLoading, user } = useAuth0()
  const logoutWithRedirect = async () => {
    try {
      dispatch(apiService.util.resetApiState())

      await persistor.purge()

      const cookies = Cookies.get()
      Object.keys(cookies).forEach((cookieName) => {
        Cookies.remove(cookieName)
      })

      window.localStorage.clear()
      window.sessionStorage.clear()

      resetHeaders()

      logout({
        logoutParams: { returnTo: window.location.origin },
      })
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  useEffect(() => {
    const refreshAuthLogic = async (failedRequest: any) => {
      const data = failedRequest?.response?.data || {}
      if (data?.code === '3013') {
        navigate('/error?code=3013')
      } else if (data?.code === String(ErrorCodes.MULTIPLE_SESSIONS)) {
        dispatch(setIsMultiSession(true))
        navigate(`/error?code=3005&redirect=${location.pathname}`)
      } else if (data?.code === '3006') {
        logoutWithRedirect()
      } else if (
        data?.code === String(ErrorCodes.INVALID_SUBSCRIPTION) &&
        window.location.pathname !== '/profile' &&
        window.location.pathname !== '/recipe'
      ) {
        navigate('/plans')
      }
      throw failedRequest
    }

    createAuthRefreshInterceptor(httpService, refreshAuthLogic, {
      statusCodes: [403],
      pauseInstanceWhileRefreshing: true,
    })
  }, [location, dispatch, navigate, logoutWithRedirect])

  const pusherRef = useRef(false)
  const imageUploadDebounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const pusherKey = import.meta.env.VITE_PUSHER_KEY
  const pusherCluster = import.meta.env.VITE_PUSHER_CLUSTER

  useEffect(() => {
    if (pusherRef.current || !isAuthenticated) return

    const sessionId = Cookies.get('session_id')
    if (!sessionId) return

    const pusher = new Pusher(pusherKey, {
      cluster: pusherCluster,
    })

    if (user) {
      Sentry.setTags({
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
        userEmail: user.email,
        userName: user.name,
      })

      Sentry.setUser({
        email: user.email,
        username: user.name,
      })
    }

    // Logout Event (global)
    const channelAuth = pusher.subscribe('auth')
    channelAuth.bind(
      'logout_event',
      (data: { logout: boolean; session_id: string }) => {
        if (data.logout && data.session_id === sessionId) {
          logoutWithRedirect()
        }
      },
    )

    const sessionChannel = pusher.subscribe(sessionId)

    sessionChannel.bind(
      'image_failed',
      (data: {
        process: string
        key: string
        message:
          | boolean
          | ReactPortal
          | ((props: ToastContentProps<{}>) => ReactNode)
          | null
          | undefined
      }) => {
        if (data.process === 'upload') {
          dispatch(setImageUploadFailed(true))
          dispatch(removeFailedImage(data.key))
          toast.error(data.message || 'Image upload failed')
        }
        if (data.process === 'calibrate' || data.process === 'dehaze') {
          dispatch(setGalleryImageLodaingMap({ [data.key]: false }))
          toast.error(
            data.message ||
              `Image ${
                data.process === 'calibrate' ? 'calibration' : 'dehazing'
              } failed`,
          )
        }
      },
    )

    sessionChannel.bind(
      'image_uploaded',
      (data: {
        process: string
        key: string
        message:
          | boolean
          | ReactPortal
          | ((props: ToastContentProps<{}>) => ReactNode)
          | null
          | undefined
      }) => {
        if (data.process === 'upload') {
          if (imageUploadDebounceTimerRef.current) {
            clearTimeout(imageUploadDebounceTimerRef.current)
          }

          imageUploadDebounceTimerRef.current = setTimeout(() => {
            const query = new URLSearchParams(window.location.search)
            const caseId = query.get('caseId')
            if (caseId) {
              dispatch(
                apiService.endpoints.getCaseDetails.initiate(
                  { caseId },
                  { forceRefetch: true },
                ) as any,
              )
            }
            imageUploadDebounceTimerRef.current = null
          }, 300)
        }
      },
    )

    pusherRef.current = true

    return () => {
      if (imageUploadDebounceTimerRef.current) {
        clearTimeout(imageUploadDebounceTimerRef.current)
      }
      pusher.disconnect()
    }
  }, [isAuthenticated, dispatch])

  useEffect(() => {
    if (location.pathname === '/fix-login') {
      clearSession('logout').finally(() => {
        logoutWithRedirect()
      })
    }
  }, [location.pathname, logoutWithRedirect])

  if (!isAuthenticated && !isLoading) {
    if (window.location.pathname === '/try') {
      loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } })
    } else {
      loginWithRedirect()
    }
    return null
  }

  const responseHandler = (response: AxiosResponse) => {
    return response
  }

  const errorHandler = (error: AxiosError) => {
    Sentry.captureException(error)
    if (error?.response?.status === 401) {
      loginWithRedirect()
    }
    throw error
  }

  httpService.interceptors.response.use(
    (response) => responseHandler(response),
    (error) => errorHandler(error),
  )

  httpService.interceptors.response.use(
    (response) => responseHandler(response),
    (error) => {
      Sentry.captureException(error)
      if (error?.response?.status === 401) {
        loginWithRedirect()
      }
      if (error?.response?.data?.code === 551) {
        window.location.href = '/error?code=551'
      } else if (
        error?.message?.includes('Network Error') ||
        error?.response?.status >= 500
      ) {
        window.location.href = '/error'
      }
      if (error?.response?.data?.code === '3005') {
        throw error
      }
      if (error?.response?.data?.code === '3006') {
        throw error
      }
      throw error
    },
  )

  return (
    <div>
      {isLoading ? (
        <div
          style={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        children
      )}
    </div>
  )
}

export default AxiosInterceptor
